import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  HelpCircle, X, ChevronDown, ChevronRight, BookOpen, Keyboard,
  Lightbulb, CircuitBoard, FileText, GitBranch, Package, Layers,
  ExternalLink, Zap,
} from 'lucide-react';

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  items: { label: string; description: string; link?: string }[];
}

const GLOBAL_SECTIONS: HelpSection[] = [
  {
    id: 'getting-started', title: 'Getting Started', icon: <Zap className="h-4 w-4" />,
    items: [
      { label: 'Create your first product', description: 'Go to Circuit Designs and click "New Design" to add your first PCB or module.', link: '/plm/products' },
      { label: 'Build a Bill of Materials', description: 'Navigate to BOMs, create a header, then add components with part numbers and quantities.', link: '/pdm/bom' },
      { label: 'Submit a change request', description: 'Use Engineering Changes to formally propose design modifications with impact analysis.', link: '/ecm/changes' },
      { label: 'Track inventory', description: 'Add components to the Component Vault to monitor stock levels and reorder points.', link: '/erp/inventory' },
    ],
  },
  {
    id: 'keyboard', title: 'Keyboard Shortcuts', icon: <Keyboard className="h-4 w-4" />,
    items: [
      { label: '⌘K / Ctrl+K', description: 'Open command palette — search anything, navigate anywhere' },
      { label: '?', description: 'Toggle this help panel' },
      { label: 'Escape', description: 'Close any open drawer, modal, or panel' },
    ],
  },
];

const PAGE_HELP: Record<string, HelpSection[]> = {
  '/plm/products': [{
    id: 'plm-tips', title: 'Product Management Tips', icon: <CircuitBoard className="h-4 w-4" />,
    items: [
      { label: 'Lifecycle stages', description: 'Products flow through Concept → Design → Prototype → Production → End of Life. Use status to track progress.' },
      { label: 'Part numbers', description: 'Use a consistent scheme like PRD-001, PRD-002. Part numbers are searchable across the system.' },
      { label: 'Complexity rating', description: 'Tag designs as Low/Medium/High to help prioritize engineering resources.' },
    ],
  }],
  '/pdm/bom': [{
    id: 'bom-tips', title: 'BOM Management Tips', icon: <Layers className="h-4 w-4" />,
    items: [
      { label: 'Reference designators', description: 'Enter comma-separated ref des (C1, C2, C3) to track component placement on PCB.' },
      { label: 'Cost rollup', description: 'Total BOM cost is calculated automatically from component unit costs × quantities.' },
      { label: 'BOM revisions', description: 'Create a new BOM revision when making changes to a released BOM — never edit a released one.' },
    ],
  }],
  '/pdm/documents': [{
    id: 'doc-tips', title: 'Document Tips', icon: <FileText className="h-4 w-4" />,
    items: [
      { label: 'Version control', description: 'Increment the version number each time you update a document. Use semantic versioning (1.0, 1.1, 2.0).' },
      { label: 'Review workflow', description: 'Set status to "In Review" when requesting approval, then "Approved" once signed off.' },
    ],
  }],
  '/ecm/changes': [{
    id: 'ecm-tips', title: 'Change Management Tips', icon: <GitBranch className="h-4 w-4" />,
    items: [
      { label: 'ECR vs ECO', description: 'An ECR (Change Request) proposes a change. Once approved, an ECO (Change Order) is created to implement it.' },
      { label: 'Impact analysis', description: 'Always estimate cost and risk before submitting. This helps reviewers prioritize.' },
      { label: 'Risk levels', description: 'High risk = affects form/fit/function. Medium = affects process. Low = documentation only.' },
    ],
  }],
  '/erp/inventory': [{
    id: 'inv-tips', title: 'Inventory Tips', icon: <Package className="h-4 w-4" />,
    items: [
      { label: 'Reorder points', description: 'Set reorder points based on lead time × daily usage. The system alerts when stock drops below this level.' },
      { label: 'Stock statuses', description: 'Statuses are computed automatically: In Stock (above reorder), Low Stock (at/below reorder), Out of Stock (zero).' },
    ],
  }],
};

export function ContextualHelp() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>('getting-started');
  const location = useLocation();
  const navigate = useNavigate();

  const pageSections = PAGE_HELP[location.pathname] || [];
  const allSections = [...pageSections, ...GLOBAL_SECTIONS];

  // Global ? key listener
  React.useEffect(() => {
    function handler(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === '?') { e.preventDefault(); setOpen((o) => !o); }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <>
      {/* Floating help button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className={cn(
            'fixed bottom-6 right-6 z-40 h-11 w-11 rounded-full',
            'bg-primary-600 text-white shadow-elevated border border-white/10',
            'hover:bg-primary-500 hover:scale-110 active:scale-95',
            'transition-all duration-200 flex items-center justify-center',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-surface'
          )}
          aria-label="Open help"
        >
          <HelpCircle className="h-5 w-5" />
        </button>
      )}

      {/* Help drawer */}
      {open && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Help">
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm animate-fade-in" onClick={() => setOpen(false)} />
          <div className="fixed top-3 right-3 bottom-3 w-full max-w-md bg-surface-raised/94 backdrop-blur-2xl border border-border rounded-[30px] shadow-modal animate-slide-right flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-5 border-b border-border bg-gradient-to-r from-primary-600 to-primary-700 flex-shrink-0">
              <div className="flex items-center gap-2 text-white">
                <BookOpen className="h-5 w-5" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/70">Contextual Help</p>
                  <h2 className="mt-1 text-xl font-semibold tracking-[-0.04em]">Help & Guidance</h2>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white transition-colors p-2 rounded-full hover:bg-white/10" aria-label="Close help">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Context indicator */}
            {pageSections.length > 0 && (
              <div className="px-5 py-2 border-b border-border bg-primary-500/5">
                <p className="text-xs text-primary-500 font-medium tracking-[-0.02em] flex items-center gap-1">
                  <Lightbulb className="h-3 w-3" />
                  Showing tips for this page
                </p>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto py-3">
              {allSections.map((section) => (
                <div key={section.id} className="border-b border-border last:border-0">
                  <button
                    onClick={() => setExpanded(expanded === section.id ? null : section.id)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-surface-overlay/70 transition-colors"
                  >
                    <span className="text-primary-500">{section.icon}</span>
                    <span className="flex-1 text-sm font-semibold tracking-[-0.02em] text-content">{section.title}</span>
                    {expanded === section.id ? <ChevronDown className="h-4 w-4 text-content-muted" /> : <ChevronRight className="h-4 w-4 text-content-muted" />}
                  </button>
                  {expanded === section.id && (
                    <div className="px-5 pb-3 space-y-2 animate-fade-in">
                      {section.items.map((item, i) => (
                        <div key={i} className="pl-7">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold tracking-[-0.02em] text-content">{item.label}</p>
                              <p className="text-xs text-content-muted mt-0.5 leading-relaxed">{item.description}</p>
                            </div>
                            {item.link && (
                              <button
                                onClick={() => { navigate(item.link!); setOpen(false); }}
                                className="text-primary-500 hover:text-primary-400 flex-shrink-0 mt-0.5"
                                title="Go to page"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-border bg-surface-overlay/45 text-xs text-content-muted flex-shrink-0">
              Press <kbd className="font-mono bg-surface-overlay px-1.5 py-0.5 rounded-full">?</kbd> to toggle help · <kbd className="font-mono bg-surface-overlay px-1.5 py-0.5 rounded-full">⌘K</kbd> for commands
            </div>
          </div>
        </div>
      )}
    </>
  );
}
